import { useState, type ReactNode } from "react"
import { Navigate, useLocation, useSearchParams } from "react-router"
import { hasSessionHint } from "@/auth/session-hint"
import { useSession } from "@/auth/session"
import { safeFrom } from "../lib/destination"

/**
 * Sign-in, registration and recovery are for visitors: a signed-in user who
 * lands on one (a bookmark, the back button, a reload) goes where `from=`
 * asked, if it is safe (`safeFrom`), else to the overview.
 *
 * Decided once: on mount when the session is known, else when the bootstrap
 * refresh answers. A sign-in completing ON this screen sets the token a moment
 * before it navigates to the real destination (`from=`, `/enrol-totp`);
 * reacting to that token would route through `/` first (DW-77).
 *
 * While the bootstrap refresh is out, the screen renders at once for a visitor
 * without the boot hint (the common anonymous visit), and is held for one
 * with it, whose static first paint is the shell's skeleton.
 */
export function RedirectIfSignedIn({ children }: { children: ReactNode }) {
  const { state, boot } = useSession()
  const [params] = useSearchParams()
  const [arrival] = useState(state)
  const signedIn = arrival === "authenticated" || (arrival === "pending" && boot === "restored")
  if (signedIn) return <Navigate to={safeFrom(params.get("from"))} replace />
  if (arrival === "pending" && boot === "pending" && hasSessionHint()) return null
  return <>{children}</>
}

/** `/enrol-totp` needs a session to enrol; without one, sign in first and come back. */
export function RequireSignedIn({ children }: { children: ReactNode }) {
  const { state } = useSession()
  const location = useLocation()
  if (state === "authenticated") return <>{children}</>
  if (state === "pending") return null
  const from = encodeURIComponent(location.pathname + location.search)
  return <Navigate to={`/sign-in?from=${from}`} replace />
}
